// Libraries
import { useState, useMemo } from "react";

// Types
type PreschoolOption = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type PreschoolMultiSelectProps = {
  options: PreschoolOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  ariaLabelledBy?: string;
};

// Component
export default function PreschoolMultiSelect({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabelledBy,
}: PreschoolMultiSelectProps) {
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter((x) =>
      x.PreschoolName.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const valueSet = new Set(value);

  const allSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((x) => valueSet.has(x.PreschoolCDSCode));
  const someSelected = filteredOptions.some((x) =>
    valueSet.has(x.PreschoolCDSCode)
  );

  const handleSelectAll = () => {
    if (allSelected) {
      const filteredCodes = new Set(
        filteredOptions.map((x) => x.PreschoolCDSCode)
      );
      onChange(value.filter((x) => !filteredCodes.has(x)));
    } else {
      const newSelections = filteredOptions
        .map((x) => x.PreschoolCDSCode)
        .filter((code) => !valueSet.has(code));
      onChange([...value, ...newSelections]);
    }
  };

  const handleToggle = (code: string) => {
    if (valueSet.has(code)) {
      onChange(value.filter((x) => x !== code));
    } else {
      onChange([...value, code]);
    }
  };

  return (
    <div
      role="group"
      aria-labelledby={ariaLabelledBy}
      style={{
        border: "1px solid #dee2e6",
        borderRadius: 6,
        overflow: "hidden",
        background: disabled ? "#f8f9fa" : "#fff",
      }}
    >
      <div style={{ padding: "8px", borderBottom: "1px solid #e2e8f0" }}>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search preschools..."
          aria-label="Search preschools"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div
        style={{
          padding: "6px 8px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <label
          className="d-flex align-items-center gap-2 mb-0"
          style={{
            cursor: disabled ? "default" : "pointer",
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "#1e293b",
          }}
        >
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={handleSelectAll}
            disabled={disabled}
            style={{
              width: "1rem",
              height: "1rem",
              accentColor: "#0d2d4a",
              cursor: disabled ? "default" : "pointer",
            }}
          />
          Select All
          {value.length > 0 && (
            <span
              style={{ fontSize: "0.8rem", color: "#374151", fontWeight: 500 }}
            >
              ({value.length} selected)
            </span>
          )}
        </label>
      </div>

      <div
        role="listbox"
        aria-multiselectable="true"
        aria-label="Preschool options"
        style={{ maxHeight: 200, overflowY: "auto" }}
      >
        {filteredOptions.length === 0 ? (
          <div
            className="text-muted text-center py-3"
            style={{ fontSize: "0.875rem" }}
          >
            No preschools found
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = valueSet.has(option.PreschoolCDSCode);
            return (
              <label
                key={option.PreschoolCDSCode}
                className="d-flex align-items-center gap-2 mb-0 px-2 py-1"
                style={{
                  cursor: disabled ? "default" : "pointer",
                  fontSize: "0.875rem",
                  color: isSelected ? "#0d2d4a" : "#1e293b",
                  background: isSelected
                    ? "rgba(13, 45, 74, 0.05)"
                    : "transparent",
                  transition: "background 0.1s",
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(option.PreschoolCDSCode)}
                  disabled={disabled}
                  style={{
                    width: "1rem",
                    height: "1rem",
                    accentColor: "#0d2d4a",
                    cursor: disabled ? "default" : "pointer",
                    flexShrink: 0,
                  }}
                />
                {option.PreschoolName}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
